
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { CreateEstimateStratigraphyData, EstimateStratigraphy } from '@/types/estimateStratigraphy';

// Utility per convertire camelCase a snake_case
function camelToSnake(str: string) {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

function convertKeysToSnakeCase(obj: any) {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    acc[camelToSnake(key)] = value;
    return acc;
  }, {} as any);
}

export const useUpdateEstimateStratigraphy = (estimateStratigraphies: EstimateStratigraphy[]) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const updateEstimateStratigraphyMutation = useMutation({
    mutationFn: async (
      data: {
        id: string,
        layersData?: any,
        stratigraphyData?: any,
        unitCost?: number,
        totalCost?: number,
        pricesUpdatedAt?: string,
        name?: string,
        area?: number,
        description?: string,
        difficultyMultiplier?: number,
        finishLevel?: string | null,
        finishCostPerSqm?: number | null,
        finishLaborMinutesPerSqm?: number | null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        finishComponentsData?: any[] | null,
      } & Partial<CreateEstimateStratigraphyData>
    ) => {
      if (!user) throw new Error('User not authenticated');

      // PROTEZIONE: Verifica lo stato del preventivo prima di procedere
      const currentItem = estimateStratigraphies.find(item => item.id === data.id);
      if (!currentItem) {
        throw new Error('Stratigrafia del preventivo non trovata');
      }

      const { data: estimateData, error: estimateError } = await supabase
        .from('estimates')
        .select('status, project_id')
        .eq('id', currentItem.estimateId)
        .single();

      if (estimateError) {
        console.error('Error fetching estimate for validation:', estimateError);
        throw new Error('Preventivo non trovato');
      }

      // F30: blocca se il preventivo è chiuso (vinto/perso/legacy contracted).
      if (estimateData.status === 'won' || estimateData.status === 'lost' || estimateData.status === 'contracted') {
        const label = estimateData.status === 'lost' ? 'perso' : 'vinto';
        throw new Error(`Impossibile modificare: preventivo ${label}`);
      }

      // Verifica accessibilita' progetto (RLS org-scoped: is_member_of). NON
      // filtrare per user_id: bloccherebbe i membri del team sui progetti altrui.
      // La scrittura vera e' comunque protetta dalla RLS org+ruolo.
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .select('id')
        .eq('id', estimateData.project_id)
        .single();

      if (projectError || !projectData) {
        throw new Error('Accesso negato al progetto');
      }

      const { id, layersData, stratigraphyData, unitCost, totalCost, pricesUpdatedAt, ...otherUpdateData } = data;

      // Base: allowed fields as before + new fields
      const allowedFields = [
        'name',
        'area',
        'unitCost',
        'description',
        'layersData',
        'stratigraphyData',
        'unitCost',
        'totalCost',
        'pricesUpdatedAt',
        // F32 Step 3: editing altezza parete post-add. Cambia il numero di
        // pezzi reali nel riepilogo materiali (useMaterialsSummary) ma NON
        // tocca unit_cost €/m² del preventivo (la "linea rossa").
        'wallHeight',
        // IVA multipla: aliquota override per-riga (null = default org).
        'vatRate',
        // Mark-up override per-riga (null = usa markup categoria org).
        'markupPct',
        // Coefficiente difficoltà per-riga (default 1.00, range 0.80–2.00).
        // Moltiplica unit_cost nel calcolo total_cost. NON è mark-up.
        'difficultyMultiplier',
        // Livello di finitura (Q1-Q4) + snapshot costo/manodopera/BOM.
        // Modificabile inline dalla tabella preventivo: il chiamante calcola
        // il nuovo unit_cost (base + costo finitura) e lo passa insieme.
        'finishLevel',
        'finishCostPerSqm',
        'finishLaborMinutesPerSqm',
        'finishComponentsData',
      ];
      let sanitizedData: any = {};
      for (const k of allowedFields) {
        if (data[k] !== undefined) sanitizedData[k] = data[k];
      }

      // Conversione camelCase → snake_case chiavi
      let dbUpdateData: any = convertKeysToSnakeCase(sanitizedData);

      // Serializzo layers_data / stratigraphy_data se oggetti o array
      if ('layers_data' in dbUpdateData && dbUpdateData.layers_data !== undefined) {
        dbUpdateData.layers_data = dbUpdateData.layers_data; // JSONB in supabase accetta oggetti e array direttamente in client
      }
      if ('stratigraphy_data' in dbUpdateData && dbUpdateData.stratigraphy_data !== undefined) {
        dbUpdateData.stratigraphy_data = dbUpdateData.stratigraphy_data;
      }

      // Ricalcola i costi se cambia area, unit_cost, difficulty_multiplier o finitura.
      // La difficolta` si applica SOLO alla MANODOPERA (base + finitura), NON ai
      // materiali: una parete in un posto difficile costa piu` mano d'opera, non
      // lastre piu` care. Formula:
      //   total = area × unit_cost + area × labor_per_sqm × (difficolta − 1)
      // con labor_per_sqm = (minuti_base + minuti_finitura)/60 × tariffa_oraria.
      if ('area' in dbUpdateData || 'unit_cost' in dbUpdateData
          || 'difficulty_multiplier' in dbUpdateData || 'finish_labor_minutes_per_sqm' in dbUpdateData) {
        const area = dbUpdateData.area ?? currentItem.area;
        const unitCost = dbUpdateData.unit_cost ?? currentItem.unitCost;
        const diff = dbUpdateData.difficulty_multiplier
          ?? (currentItem as { difficultyMultiplier?: number }).difficultyMultiplier
          ?? 1;
        if (Math.abs(diff - 1) < 1e-9) {
          dbUpdateData.total_cost = area * unitCost;
        } else {
          // Tariffa oraria (globale, come useOfferNumber/fetchFinishCostSnapshot)
          const { data: rateRow } = await supabase
            .from('configurator_settings')
            .select('value')
            .eq('key', 'cost_per_hour')
            .maybeSingle();
          const rate = parseFloat((rateRow as { value?: string } | null)?.value ?? '30') || 30;
          const baseMin = Number(
            (currentItem as { stratigraphyData?: { installation_time_per_sqm?: number } })
              .stratigraphyData?.installation_time_per_sqm ?? 0,
          );
          const finishMin = Number(
            dbUpdateData.finish_labor_minutes_per_sqm
              ?? (currentItem as { finishLaborMinutesPerSqm?: number | null }).finishLaborMinutesPerSqm
              ?? 0,
          );
          const laborPerSqm = ((baseMin + finishMin) / 60) * rate;
          dbUpdateData.total_cost = area * unitCost + area * laborPerSqm * (diff - 1);
        }
      }
      if ('total_cost' in dbUpdateData && dbUpdateData.total_cost === undefined) {
        delete dbUpdateData.total_cost;
      }

      if (pricesUpdatedAt) {
        dbUpdateData.prices_updated_at = pricesUpdatedAt;
      }

      // Esegui mutation
      const { data: result, error } = await supabase
        .from('estimate_stratigraphies')
        .update(dbUpdateData)
        .eq('id', id)
        .maybeSingle();

      if (error) {
        console.error('Error updating estimate stratigraphy snapshot:', error, dbUpdateData);
        throw error;
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimate-stratigraphies'] });
      queryClient.invalidateQueries({ queryKey: ['estimate'] });
      toast.success('Stratigrafia del preventivo aggiornata!');
    },
    onError: (error) => {
      console.error('Error updating estimate stratigraphy snapshot:', error);
      if (error.message.includes('contrattualizzato')) {
        toast.error('Impossibile modificare: preventivo contrattualizzato');
      } else {
        toast.error('Errore nell\'aggiornamento della stratigrafia del preventivo');
      }
    },
  });

  return {
    updateEstimateStratigraphy: (
      id: string,
      data: {
        layersData?: any,
        stratigraphyData?: any,
        unitCost?: number,
        totalCost?: number,
        pricesUpdatedAt?: string,
        name?: string,
        area?: number,
        description?: string,
        difficultyMultiplier?: number,
        finishLevel?: string | null,
        finishCostPerSqm?: number | null,
        finishLaborMinutesPerSqm?: number | null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        finishComponentsData?: any[] | null,
      } & Partial<CreateEstimateStratigraphyData>
    ) => {
      updateEstimateStratigraphyMutation.mutate({ id, ...data });
    },
    isUpdating: updateEstimateStratigraphyMutation.isPending,
  };
};
